import React, { useState, useEffect } from 'react';
import { Clock, Trash2, Bell } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import ImportExportSection from './ImportExportSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = async () => {
  if (typeof chrome !== 'undefined' && chrome.storage) {
    const { accessToken } = await chrome.storage.local.get('accessToken');
    if (accessToken && accessToken !== 'session_active') {
      return { Authorization: `Bearer ${accessToken}` };
    }
  }
  return {};
};

export default function SettingsTab() {
  const { user } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.get(`${API_URL}/api/reminders`, {
        withCredentials: true,
        headers
      });
      setReminders(data);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${API_URL}/api/reminders/${reminderId}`, {
        withCredentials: true,
        headers
      });
      toast.success('Reminder deleted');
      fetchReminders();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        toast.success('Notifications enabled');
      } else {
        toast.error('Notifications denied');
      }
    } else {
      toast.error('Notifications not supported');
    }
  };

  return (
    <div className="space-y-4" data-testid="settings-tab">
      {/* Profile Section */}
      <div className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm p-4">
        <h3 className="text-xs tracking-wider uppercase text-[#78716C] mb-3">Profile</h3>
        <div className="space-y-2">
          <div>
            <p className="text-xs text-[#78716C]">Name</p>
            <p className="text-sm font-medium text-[#1C1917]">{user?.name}</p>
          </div>
          <div>
            <p className="text-xs text-[#78716C]">Email</p>
            <p className="text-sm font-medium text-[#1C1917]">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Import/Export */}
      <ImportExportSection />

      {/* Notifications */}
      <div className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm p-4">
        <h3 className="text-xs tracking-wider uppercase text-[#78716C] mb-3">Notifications</h3>
        <button
          onClick={requestNotificationPermission}
          className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors border border-[#E7E5E4] bg-white text-[#1C1917] hover:bg-[#FAFAF9] w-full"
          data-testid="enable-notifications-button"
        >
          <Bell size={16} className="mr-2" />
          Enable Browser Notifications
        </button>
      </div>

      {/* Active Reminders */}
      <div className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm p-4">
        <h3 className="text-xs tracking-wider uppercase text-[#78716C] mb-3">Active Reminders</h3>
        
        {loading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2C4C3B] mx-auto"></div>
          </div>
        ) : reminders.length === 0 ? (
          <div className="text-center py-4">
            <Clock size={32} className="mx-auto text-[#E7E5E4] mb-2" />
            <p className="text-sm text-[#78716C]">No active reminders</p>
          </div>
        ) : (
          <div className="space-y-2">
            {reminders.map((reminder) => (
              <div
                key={reminder.reminder_id}
                className="flex items-center justify-between p-3 border border-[#E7E5E4] rounded-lg"
                data-testid={`reminder-${reminder.reminder_id}`}
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#1C1917]">Prompt ID: {reminder.prompt_id.substring(0, 12)}...</p>
                  <p className="text-xs text-[#78716C]">
                    {reminder.schedule_type} at {reminder.schedule_time}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteReminder(reminder.reminder_id)}
                  className="text-[#78716C] hover:text-red-600 transition-colors"
                  data-testid="delete-reminder-button"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Install Instructions */}
      <div className="bg-[#F5F2EA] rounded-xl p-4">
        <h3 className="text-xs tracking-wider uppercase text-[#78716C] mb-2">Extension Info</h3>
        <p className="text-xs text-[#57534E] leading-relaxed">
          To install this extension, go to chrome://extensions/, enable Developer Mode, and load the unpacked extension folder.
        </p>
      </div>
    </div>
  );
}